import BasicButton from "@/components/core/buttons/BasicButton";
import EditOrAddContactModal from "@/components/core/modals/EditOrAddContactModal";
import ImportContactModal from "@/components/core/modals/ImportContactModal";
import {
  requestCreateContact,
  requestDeleteContact,
  requestExportContact,
  requestImportContacts,
  requestSearchContactsForOffice,
  requestUpdateContact,
} from "@/state/contact/ContactsEffects";
import { CONTACT_EXPORT_FILENAME, DEFAULT_PAGE_SIZE } from "@/utils/Constant";
import { FileHelper } from "@/utils/helpers/file-helper";
import { PhoneHelper } from "@/utils/helpers/phone-helper";
import { toastError } from "@/utils/helpers/toast-helper";
import useOfficesToDisplay from "@/utils/hooks/useOfficesToDisplay";
import PageLayout from "@components/core/layouts/PageLayout";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import InfoModal from "@components/core/modals/InfoModal";
import CardSection from "@components/core/section/CardSection";
import BasicTable from "@components/core/tables/BasicTable";
import BasicTabs from "@components/core/tabs/BasicTabs";
import { ROUTES } from "@routes/Routes";
import DownloadIcon from "@static/core/icons/icon-download.svg?react";
import { Contact, File } from "@type/core/dashboard/dashboard.types";
import { columns } from "@views/core/extranet/menu/offices/contacts/columns/ContactsColumns";
import { Row, Select, Space } from "antd";
import Search from "antd/es/input/Search";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface SelectOptions {
  label: string;
  value: string;
}

const ContactsScreen: FunctionComponent = () => {
  const { t } = useTranslation();

  const { officesToDisplay, selectedOffice, handleTabClick, activeKey } =
    useOfficesToDisplay();
  const officeId = selectedOffice?.id;

  const hasOfficeContactsDisabled =
    selectedOffice && selectedOffice.isContactDisabled;
  const [search, setSearch] = useState<string>("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectOptions, setSelectOptions] = useState<SelectOptions[]>([]);
  const [isDeleteContactOpen, setIsDeleteContactOpen] =
    useState<boolean>(false);
  const [isImportContactOpen, setIsImportContactOpen] =
    useState<boolean>(false);
  const [isEditOrAddContactOpen, setIsEditOrAddContactOpen] =
    useState<boolean>(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [contact, setContact] = useState<Contact>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const filteredContacts = contacts.filter((contact) => {
      const combinedString =
        `${contact.firstname} ${contact.lastname} ${contact.email} ${contact.companyName} ${contact.phone} ${contact.category}`.toLowerCase();
      return combinedString.includes(search);
    });
    setFilteredContacts(filteredContacts);
  }, [search, contacts]);

  useEffect(() => {
    const filteredContacts = contacts.filter((contact) => {
      return contact.category
        ?.split("|")
        .some((cat) => selectedCategories.includes(cat));
    });
    setFilteredContacts(
      selectedCategories.length > 0 ? filteredContacts : contacts,
    );
  }, [selectedCategories, contacts]);

  useEffect(() => {
    if (officeId) {
      setIsLoading(true);
      void requestSearchContactsForOffice({ id: officeId }).finally(() => {
        setIsLoading(false);
      });
    }
  }, [officeId]);

  useEffect(() => {
    return requestSearchContactsForOffice.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setContacts(result.data.contacts || []);
        // Set select options for the category select
        const selectOptionsFromContactsCategory: SelectOptions[] = [];
        result.data.contacts.forEach((contact) => {
          const categories = contact.category?.split("|");
          if (categories) {
            categories.forEach((category) => {
              if (category !== "") {
                selectOptionsFromContactsCategory.push({
                  label: category,
                  value: category,
                });
              }
            });
          }
        });
        // Remove duplicate category
        setSelectOptions(
          selectOptionsFromContactsCategory.reduce(
            (acc, item) => {
              const seen = acc.seen;
              if (!seen.has(item.value)) {
                seen.add(item.value);
                acc.result.push(item);
              }
              return acc;
            },
            { seen: new Set<string>(), result: [] as SelectOptions[] },
          ).result,
        );
      } else {
        toastError(t("error.toast.contactLoading"));
      }
    });
  });

  useEffect(() => {
    return requestDeleteContact.done.watch(({ result }) => {
      if (result.ok && result.data && officeId) {
        void requestSearchContactsForOffice({ id: officeId });
      } else {
        toastError(t("error.toast.contactDelete"));
      }
    });
  });

  useEffect(() => {
    return requestImportContacts.done.watch(({ result }) => {
      if (result.ok && result.data?.contacts) {
        const newContacts: Contact[] = result.data.contacts ?? [];
        setContacts((prevState) => {
          const existingIds = new Set(prevState.map((contact) => contact.id));

          // Filter out new contacts that have IDs already present in the existing contacts
          const filteredContacts =
            newContacts && newContacts.length > 0
              ? newContacts.filter(
                  (contact) => !existingIds.has(contact.id ?? ""),
                )
              : [];

          return [...prevState, ...filteredContacts];
        });
      } else {
        toastError(t("error.toast.contactImport"));
      }
      setIsLoading(false);
    });
  });

  useEffect(() => {
    return requestUpdateContact.done.watch(({ result }) => {
      if (result.ok && result.data && officeId) {
        setContact(undefined);
        void requestSearchContactsForOffice({ id: officeId });
      } else {
        toastError(t("error.toast.contactModify"));
      }
    });
  });

  useEffect(() => {
    return requestCreateContact.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setContact(undefined);
        setContacts((prevState) => {
          if (result.data) {
            return [...prevState, result.data];
          }
          return prevState;
        });
      } else {
        toastError(t("error.toast.contactCreate"));
      }
    });
  });

  useEffect(() => {
    return requestExportContact.done.watch(({ result }) => {
      if (result.ok && result.data) {
        FileHelper.createDownloadFileFromBlob(
          result.data,
          CONTACT_EXPORT_FILENAME,
          result.headers,
        );
      } else {
        toastError(t("error.toast.contactExport"));
      }
      setIsLoading(false);
    });
  });

  const handleDeleteContact = () => {
    if (contact?.id) {
      void requestDeleteContact({ id: contact.id });
    }
  };

  const handleFilesChange = (files: File[]) => {
    setIsLoading(true);
    setIsImportContactOpen(false);
    files.forEach((file) => {
      if (file.file && officeId) {
        const formData = new FormData();
        formData.append("file", file.file);
        formData.append("officeId", officeId);
        void requestImportContacts({ dto: formData });
      }
    });
  };

  const handleRemoveCategory = (contact: Contact, categoryToRemove: string) => {
    const category =
      contact.category &&
      contact.category
        .split("|")
        .filter((c) => c != categoryToRemove)
        .join("|");
    const updatedContact: Contact = {
      ...contact,
      category,
    };
    void requestUpdateContact({ dto: updatedContact, id: contact.id });
  };

  const handleEditOrAddContact = (savedContact?: Contact) => {
    if (!savedContact || !officeId) {
      return;
    }
    savedContact.id = contact?.id;
    savedContact.officeId = officeId;
    savedContact.phone =
      PhoneHelper.removePhoneWhitespace(savedContact.phone) ?? "";

    if (!contact) {
      void requestCreateContact({ dto: savedContact });
    } else {
      void requestUpdateContact({ dto: savedContact, id: savedContact.id });
    }
  };

  const handleExportContacts = () => {
    setIsLoading(true);
    void requestExportContact({
      dto: {
        officeId,
        search,
        page: 1,
        categories:
          selectedCategories.length > 0 ? selectedCategories : undefined,
        sort: { order: 1, type: "lastname" },
      },
    });
  };

  return (
    <PageLayout container returnRoute={ROUTES.extranet.home.generate()}>
      <div data-test="Base de contacts">
        <CardSection
          title={
            <>
              {t("contacts.title")}
              <InfoModal value="contact" info />
            </>
          }
        >
          <BasicTabs
            activeKey={activeKey}
            onTabClick={handleTabClick}
            tabs={officesToDisplay.map((office) => {
              return {
                title: office.alias,
                content: (
                  <>
                    {!!hasOfficeContactsDisabled && (
                      <h3 className="text-tertiary text-center mt-48">
                        {t("signature.addContactModal.contactDisabled")}
                      </h3>
                    )}
                    {!hasOfficeContactsDisabled && (
                      <>
                        <div className="d-flex flex-column flex-md-row align-items-center justify-content-center gap-24 mb-24">
                          <BasicButton
                            text={t("contacts.addContact")}
                            className="mw-340"
                            onClick={() => {
                              setContact(undefined);
                              setIsEditOrAddContactOpen(true);
                            }}
                          />
                          <span className="text-black font-large px-8">
                            {t("common.or")}
                          </span>
                          <BasicButton
                            text={t("contacts.importContact")}
                            className="mw-340"
                            onClick={() => setIsImportContactOpen(true)}
                          />
                        </div>
                        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-md-center gap-8 mb-16">
                          <span>{t("contacts.searchContact")}</span>
                          <Search
                            id="filterSearch"
                            placeholder={t("contacts.researchContact")}
                            value={search}
                            onChange={(e) => {
                              e.preventDefault();
                              setSearch(e.target.value.toLowerCase());
                            }}
                            className="search ml-md-8 w-md-50"
                          />
                        </div>
                        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-md-center gap-8 mb-24">
                          <span>{t("contacts.searchCategory")}</span>
                          <Space
                            className="search ml-md-8 w-md-50"
                            direction="vertical"
                          >
                            <Select
                              className="ant-select-multiple-custom"
                              value={selectedCategories}
                              mode="multiple"
                              allowClear
                              placeholder={t(
                                "contacts.fields.category.placeholder",
                              )}
                              onChange={setSelectedCategories}
                              options={selectOptions}
                              notFoundContent={null}
                            />
                          </Space>
                        </div>
                        <div className="min-height-table">
                          <BasicTable
                            isLoading={isLoading}
                            columns={columns(
                              setIsDeleteContactOpen,
                              setContact,
                              handleRemoveCategory,
                              setIsEditOrAddContactOpen,
                            )}
                            dataSource={filteredContacts}
                            pagination={{ pageSize: DEFAULT_PAGE_SIZE }}
                            emptyTextNull
                          />
                        </div>
                        <Row className="justify-content-end mt-16">
                          <a
                            href=""
                            onClick={(e) => {
                              e.preventDefault();
                              handleExportContacts();
                            }}
                          >
                            <DownloadIcon className="picto icon-30" />
                          </a>
                        </Row>
                      </>
                    )}
                  </>
                ),
              };
            })}
          />
        </CardSection>
        <ConfirmModal
          visible={isDeleteContactOpen}
          onCancel={() => setIsDeleteContactOpen(false)}
          onOk={() => {
            handleDeleteContact();
            setIsDeleteContactOpen(false);
          }}
          title={t("contacts.deleteContact")}
        />
        {/* TODO: MODAL -> showAddContactModal, showEditContactModal */}
        <ImportContactModal
          visible={isImportContactOpen}
          onCancel={() => setIsImportContactOpen(false)}
          onFilesChange={handleFilesChange}
        />
        <EditOrAddContactModal
          contact={contact}
          visible={isEditOrAddContactOpen}
          onCancel={() => setIsEditOrAddContactOpen(false)}
          onConfirm={handleEditOrAddContact}
        />
      </div>
    </PageLayout>
  );
};

export default ContactsScreen;
